import axios from 'axios'
import CryptoJS from 'crypto-js'
import { store } from '../redux/store'
import endpoints from './endpoints'

const Config = {
  ENC_KEY: process.env.REACT_APP_ENC_KEY ?? '',
  ENC_IV: process.env.REACT_APP_ENC_IV ?? '',
  API_KEY: process.env.REACT_APP_API_KEY ?? '',
  BASE_URL: process.env.REACT_APP_API_ENDPOINT ?? '',
  PNDS_BASE_URL: process.env.REACT_APP_PNDS_BASE_URL ?? '',
  PNDS_API_KEY: process.env.REACT_APP_PNDS_API_KEY ?? '',
}

const nonEncryptionRoutes = [
  endpoints.GET_MEDICAL_CONDITIONS,
  endpoints.GET_PINCODE_DETAILS,
  endpoints.REQUEST_CALLBACK,
  endpoints.SUBMIT_PATIENT_DETAILS,
]

const routesWithAuthHeaders = [
  endpoints.Auth.LOGIN_OR_REGISTER_PATIENT,
  endpoints.Auth.SEND_OTP,
  endpoints.Auth.VERIFY_OTP,
]

const instance = axios.create({
  baseURL: Config.BASE_URL,
})

export const getEncryptedText = (data) => {
  const truncHexKey = CryptoJS.SHA256(Config.ENC_KEY).toString().substr(0, 32)
  const key = CryptoJS.enc.Utf8.parse(truncHexKey)
  const iv = CryptoJS.enc.Utf8.parse(Config.ENC_IV)

  const ciphertext = CryptoJS.AES.encrypt(JSON.stringify(data), key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
  })

  return ciphertext.toString()
}

export const getDecryptedData = (cipher) => {
  const truncHexKey = CryptoJS.SHA256(Config.ENC_KEY).toString().substr(0, 32)
  const key = CryptoJS.enc.Utf8.parse(truncHexKey)
  const iv = CryptoJS.enc.Utf8.parse(Config.ENC_IV)

  const decryptedData = CryptoJS.AES.decrypt(cipher, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
  })

  return JSON.parse(decryptedData.toString(CryptoJS.enc.Utf8))
}

instance.interceptors.request.use(
  async (config) => {
    const IS_ENCRYPTED = !nonEncryptionRoutes.includes(config.url)
    const IS_AUTH_API = routesWithAuthHeaders.includes(config.url)

    const isFormData = config.data instanceof FormData

    const IS_PNDS = config.isPnds ?? false

    // ATTACH TOKEN
    const {
      auth: { userDetails },
    } = store.getState()
    if (userDetails?.data?.token) {
      config.headers['token'] = userDetails?.data?.token
    }
    if (!IS_ENCRYPTED) {
      config.headers['Content-Type'] = 'application/json'
    } else if (!isFormData) {
      config.headers['Content-Type'] = 'text/plain'
      config.data = getEncryptedText(config.data)
    } else {
      config.headers['Content-Type'] = 'multipart/form-data'
    }

    if (IS_PNDS) {
      config.headers['api-key'] = Config.PNDS_API_KEY
      config.baseURL = Config.PNDS_BASE_URL
    } else {
      config.headers['api-key'] = Config.API_KEY
    }

    if (IS_AUTH_API) {
      config.headers['userFrom'] = 'PSP'
      config.headers['source'] = 'GoodFlip'
      config.headers['platform'] = 'web'
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

instance.interceptors.response.use(
  (response) => {
    const IS_ENCRYPTED = !nonEncryptionRoutes.includes(response.config.url)

    //IF PNDS, no encryption decryption
    const IS_PNDS = response?.config?.isPnds ?? false
    if (IS_PNDS) {
      return response
    }

    let returnData = response.data
    if (
      response.headers['content-type']?.includes('application/json') ||
      response.headers['content-type']?.includes('text/')
    ) {
      if (IS_ENCRYPTED) {
        returnData = getDecryptedData(returnData)
      }
    }

    if (returnData.code === 0) {
      return Promise.reject(new Error(returnData.message || 'Unknown Error'))
    }

    return returnData
  },
  (error) => {
    if (axios.isAxiosError(error)) {
      let errMsg = ''
      if (error?.response?.data?.detail) {
        errMsg = error?.response?.data?.detail
      } else {
        errMsg = getDecryptedData(error?.response?.data)?.message
      }
      error.message = errMsg
      if (
        (errMsg && ['Invalid Token'].includes(errMsg)) ||
        (error.status && [401].includes(error.status))
      ) {
        console.log('session expired', error)
        // setTimeout(() => {
        //   if (typeof window !== 'undefined') {
        //     const therapyArea = localStorage.getItem(THERAPY_AREA_STORE_KEY);
        //     const conditionName = localStorage.getItem(CONDITION_NAME_STORE_KEY);
        //     store.dispatch(resetAuth());
        //     localStorage.setItem('previousRoute', window.location.pathname);
        //     localStorage.removeItem('persist:root');
        //     redirect(`/${therapyArea}/${conditionName}/login`);
        //   }
        // }, 10);
      }
    }
    return Promise.reject(error)
  },
)

const makeRequest = async (method, url, params, data, isPnds = false) => {
  try {
    const response = await instance({
      method,
      url,
      params,
      data,
      isPnds,
    })

    return response.data
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

const handleApiError = (error) => {
  if (axios.isCancel(error)) {
    // Request was canceled
    console.warn('Request canceled:', error.message)
  } else if (error.response) {
    // The request was made, but the server responded with an error
    console.error('API error - Status:', error.response.status)
    console.error('Response data:', error.response.data)
    console.error('Headers:', error.response.headers)
  } else if (error.request) {
    // The request was made, but no response was received
    console.error('No response received. Request:', error.request)
  } else {
    // Something happened in setting up the request that triggered an error
    console.error('Error setting up request:', error.message)
  }
}

export default makeRequest
